import { ArrowLeft, MessageSquare } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { type CardSeen, type GameDetail, type MatchDetail as MatchDetailData } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CardPreview } from '@/domain/CardPreview';
import { useMatch } from '@/hooks/useArena';
import { buildGamePrompt } from '@/lib/game-prompt';
import { lifeChartRows } from '@/lib/life-chart';
import { confidenceText, resultTone } from '@/lib/stats';
import { cn } from '@/lib/utils';
import { WidgetContainer } from '@/widgets/_base/WidgetContainer';
import { UNKNOWN_REASON_TEXT } from '@/widgets/recent-matches/Widget';

export function MatchDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useMatch(id);
  const match = data?.match;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="rounded-lg border border-ugin-danger/40 bg-ugin-danger/10 p-6 text-sm text-ugin-danger">
        <p className="font-medium">This match can't be found.</p>
        <p className="mt-1 text-ugin-text-muted">
          <Link to="/matches" className="text-ugin-accent underline">
            Back to matches
          </Link>
          .
        </p>
      </div>
    );
  }

  const tone = resultTone(match.result);
  const prompt = buildGamePrompt(match);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Link
            to="/matches"
            className="inline-flex items-center gap-1 text-xs text-ugin-text-muted hover:text-ugin-text"
          >
            <ArrowLeft className="size-3" />
            All matches
          </Link>
          <h1 className="font-display text-4xl tracking-wide text-ugin-text">
            {match.opponentName ?? 'Unknown opponent'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-ugin-text-muted">
            <Badge variant={tone === 'win' ? 'accent' : 'default'} className="uppercase">
              {tone}
            </Badge>
            <span>·</span>
            <span>{match.format}</span>
            {match.deckName ? (
              <>
                <span>·</span>
                <span>{match.deckName}</span>
              </>
            ) : null}
            {match.opponentRank ? (
              <>
                <span>·</span>
                <span>{match.opponentRank}</span>
              </>
            ) : null}
          </div>
        </div>

        <Link
          to={`/chat?prompt=${encodeURIComponent(prompt)}`}
          className="inline-flex items-center gap-2 rounded-lg border border-ugin-border bg-ugin-surface px-3 py-2 text-sm text-ugin-text transition-colors hover:bg-ugin-surface-elevated"
        >
          <MessageSquare className="size-4" />
          Ask Ugin about this game
        </Link>
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-7">
          {match.games.length === 0 ? (
            <WidgetContainer
              title="No game record"
              empty
              emptyTitle="This match has no reconstructed game"
              emptyDescription="It was ingested before the game-level reader existed, or the reducer's result disagreed with Arena's own, so no derived statistics were published."
            />
          ) : (
            match.games.map((game) => <GamePanel key={game.gameNumber} game={game} />)
          )}
        </div>
        <div className="xl:col-span-5">
          <ClassificationPanel match={match} />
        </div>
      </div>
    </div>
  );
}

function GamePanel({ game }: { game: GameDetail }) {
  const facts = [
    game.onPlay === null ? null : game.onPlay ? 'on the play' : 'on the draw',
    `${String(game.turns)} turns`,
    game.myMulligans === 0
      ? 'kept seven'
      : `${String(game.myMulligans)} mulligan${game.myMulligans === 1 ? '' : 's'}`,
    game.oppMulligans === 0 ? 'they kept seven' : `they took ${String(game.oppMulligans)}`,
  ].filter((f): f is string => f !== null);

  return (
    <WidgetContainer title={`Game ${String(game.gameNumber)}`}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-ugin-text-muted">
          {facts.map((fact, i) => (
            <span key={fact}>
              {i > 0 ? '· ' : ''}
              <span className="text-ugin-text">{fact}</span>
            </span>
          ))}
        </div>

        <div className="flex items-baseline gap-4 font-mono text-sm">
          <span className="text-ugin-text-muted">Final life</span>
          <span className="text-ugin-text">
            you {game.finalLife.me} · them {game.finalLife.opp}
          </span>
        </div>

        <LifeChart game={game} />

        <CardColumn label="They revealed" cards={game.cards.opp} showTurn />
        <CardColumn label="You held or cast" cards={game.cards.me} />

        {game.partial || game.gaps > 0 ? (
          <p className="text-xs text-ugin-warn">
            {game.partial
              ? 'Ugin joined this game part-way, so the early turns are incomplete. '
              : ''}
            {game.gaps > 0 ? `${String(game.gaps)} gaps in the stream.` : ''}
          </p>
        ) : null}
      </div>
    </WidgetContainer>
  );
}

/**
 * A small inline chart rather than a charting library: the shape is two lines,
 * and the data is already computed and tested by `lifeChartRows`.
 */
function LifeChart({ game }: { game: GameDetail }) {
  const rows = lifeChartRows(game.lifeTrajectory);
  if (rows.length < 2) return null;

  const width = 100;
  const height = 34;
  const maxLife = Math.max(20, ...rows.map((r) => Math.max(r.me, r.opp)));
  const x = (i: number): number => (i / (rows.length - 1)) * width;
  const y = (life: number): number => height - (Math.max(0, life) / maxLife) * height;
  const path = (pick: (r: (typeof rows)[number]) => number): string =>
    rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(pick(r)).toFixed(1)}`).join(' ');

  return (
    <figure className="space-y-1">
      <svg
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        preserveAspectRatio="none"
        className="h-16 w-full"
        role="img"
        aria-label={`Life totals over ${String(rows.length)} turns`}
      >
        <path d={path((r) => r.opp)} fill="none" stroke="var(--color-ugin-danger)" strokeWidth="1" />
        <path d={path((r) => r.me)} fill="none" stroke="var(--color-ugin-accent)" strokeWidth="1" />
      </svg>
      <figcaption className="flex gap-3 text-xs text-ugin-text-faint">
        <span className="text-ugin-accent">you</span>
        <span className="text-ugin-danger">them</span>
        <span>
          turns {rows[0]?.turn}–{rows[rows.length - 1]?.turn}
        </span>
      </figcaption>
    </figure>
  );
}

function CardColumn({
  label,
  cards,
  showTurn = false,
}: {
  label: string;
  cards: CardSeen[];
  showTurn?: boolean;
}) {
  if (cards.length === 0) {
    return (
      <div className="space-y-1">
        <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">{label}</h3>
        <p className="text-sm text-ugin-text-muted">Nothing recorded.</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">{label}</h3>
      <ul aria-label={label} className="flex flex-wrap gap-1.5">
        {cards.map((c) => (
          <li
            key={`${String(c.grpId)}-${c.cardName ?? 'unknown'}`}
            className={cn(
              'rounded-md border px-2 py-1 text-xs',
              c.isLand
                ? 'border-ugin-border/30 bg-ugin-bg-deep/40 text-ugin-text-faint'
                : 'border-ugin-border/50 bg-ugin-surface/50 text-ugin-text',
            )}
          >
            {c.cardName === null ? (
              <span className="italic text-ugin-text-faint">grpId {c.grpId}</span>
            ) : (
              <CardPreview name={c.cardName} />
            )}
            {c.maxCopiesSeen > 1 ? (
              <span className="ml-1 text-ugin-text-faint">×{c.maxCopiesSeen}</span>
            ) : null}
            {showTurn ? (
              <span className="ml-1 text-ugin-text-faint">
                turn {c.firstPublicTurn ?? c.firstSeenTurn}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ClassificationPanel({ match }: { match: MatchDetailData }) {
  const classification = match.classification;

  if (classification === null) {
    return (
      <WidgetContainer
        title="Opponent"
        empty
        emptyTitle="Not classified yet"
        emptyDescription="The nightly meta refresh characterizes archetypes and labels matches. Run it, or wait for 03:30."
      />
    );
  }

  if (classification.kind === 'unknown') {
    const reason = UNKNOWN_REASON_TEXT[classification.reason] ?? 'Ugin could not name this deck.';
    return (
      <WidgetContainer title="Opponent">
        <div className="space-y-3 text-sm">
          <p className="font-medium text-ugin-text">Ugin would not name this deck.</p>
          <p className="text-ugin-text-muted">{reason}</p>
          {classification.cardsSeen.length > 0 ? (
            <div className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">
                What it had to go on
              </h3>
              <ul aria-label="Cards seen" className="flex flex-wrap gap-1.5">
                {classification.cardsSeen.map((name) => (
                  <li
                    key={name}
                    className="rounded-md border border-ugin-border/50 bg-ugin-surface/50 px-2 py-1 text-xs text-ugin-text"
                  >
                    <CardPreview name={name} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {classification.alternatives.length > 0 ? (
            <p className="text-xs text-ugin-text-faint">
              Closest fits: {classification.alternatives.map((a) => a.archetype).join(', ')}. None
              cleared the margin Ugin needs before it will put a name on a deck.
            </p>
          ) : null}
        </div>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer title="Opponent">
      <div className="space-y-4 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-display text-2xl tracking-wide text-ugin-text">
            {classification.archetype}
          </span>
          <span className="font-mono text-ugin-accent">
            {confidenceText(classification.confidence)}
          </span>
        </div>

        <div className="space-y-1.5">
          <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">
            Evidence — why Ugin thinks so
          </h3>
          <ul aria-label="Evidence" className="space-y-1">
            {classification.evidence.map((e) => (
              <li key={e.card} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="text-ugin-text">
                  <CardPreview name={e.card} />{' '}
                  <span className="text-ugin-text-faint">turn {e.firstSeenTurn}</span>
                </span>
                <span className="shrink-0 text-ugin-text-faint">
                  {e.role} · {Math.round(e.frequency * 100)}% of lists
                </span>
              </li>
            ))}
          </ul>
        </div>

        {classification.alternatives.length > 0 ? (
          <div className="space-y-1">
            <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">Also possible</h3>
            <ul className="space-y-0.5">
              {classification.alternatives.map((a) => (
                <li
                  key={a.archetype}
                  className="flex justify-between gap-3 text-xs text-ugin-text-muted"
                >
                  <span>{a.archetype}</span>
                  <span className="font-mono">{confidenceText(a.confidence)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {classification.centroidComputedAt ? (
          <p className="text-xs text-ugin-text-faint">
            Against archetype data from{' '}
            {new Date(classification.centroidComputedAt).toLocaleDateString()}.
          </p>
        ) : null}
      </div>
    </WidgetContainer>
  );
}
